/**
 * useShellTargetActions - what the target panel and the log do to the table.
 *
 * - RESET the target to full health (or a horde to full magnitude), clearing
 *   its criticals and conditions - one undo step, refused while it owes a
 *   reaction.
 * - PIN a measurement: the map's cover written MANUAL (a person's ruling,
 *   which outranks the map until cleared) and the range band chosen. Cover
 *   from the live map is projected per attacker and never stored.
 *   `coverPinned` is whether the current target carries such a ruling.
 * - CLEAR the log being looked at (the shared feed and your own are two
 *   tabs, so clearing both at once stopped being predictable), and COPY it
 *   through `copyText`, since `navigator.clipboard` does not exist on the
 *   tailnet's non-secure origin.
 *
 * Extracted from DeathWatchSimulator; the log lines are unchanged.
 */
import { useCallback } from 'react';
import { pinnedValues } from '../engine/battlefield/derived';
import { formatMetres } from '../engine/battlefield/measure';
import { formatForClipboard } from '../engine/narrativeFormatter';
import copyText from '../utils/copyText';
import { infoLine } from './shellCombatant';

/** A target restored to full, and the line that says so. */
export function resetOf(tgt) {
  if (tgt.horde?.isHorde) {
    return {
      target: {
        ...tgt,
        horde: { ...tgt.horde, magnitude: tgt.horde.maxMagnitude },
        criticalDamage: [],
        conditions: [],
      },
      text: 'Reset to full magnitude (' + tgt.horde.maxMagnitude + ').',
    };
  }
  return {
    target: { ...tgt, wounds: { ...tgt.wounds, current: tgt.wounds.max }, criticalDamage: [], conditions: [] },
    text: 'Reset to full health (' + tgt.wounds.max + ' wounds).',
  };
}

/** Does this target carry a cover somebody pinned by hand? */
export function isCoverPinned(target) {
  const cover = target?.cover;
  return (cover?.source ?? 'manual') === 'manual' && !!cover?.level && cover.level !== 'none';
}

/** The measurement's log line. */
export function measuredText(measurement, band, cover) {
  return (
    `📐 Measured ${formatMetres(measurement.metres)}` +
    (band ? ` - ${band} range` : ' - out of range') +
    (cover.level !== 'none' ? `, ${cover.level} cover (AP ${cover.ap})` : ', no cover') +
    (measurement.blocked ? '. Line of sight is BLOCKED.' : '.')
  );
}

/** The reset, behind its own undo step. */
function useResetTarget(d) {
  const { currentTab, isLocked, isDefencePending, capturePreActionSnapshot, pushHistory, updateTab, activeTab } = d;
  const { historyTrack } = d;
  return useCallback(() => {
    if (!currentTab || isLocked('resetTarget')) return;
    if (isDefencePending && isDefencePending(activeTab)) return;
    capturePreActionSnapshot();
    pushHistory();
    updateTab(activeTab, (t) => {
      const reset = resetOf(t.target);
      return { ...t, target: reset.target, narrativeLog: [...t.narrativeLog, infoLine(reset.text)] };
    });
    historyTrack.current = true;
  }, [
    currentTab,
    isLocked,
    isDefencePending,
    capturePreActionSnapshot,
    pushHistory,
    updateTab,
    activeTab,
    historyTrack,
  ]);
}

/** Pin the map's cover and band onto the target, when the table allows. */
function useApplyMeasurement({
  battlefieldDerivedValues,
  updateCombatantState,
  activeTab,
  setRangeBand,
  addNarrative,
}) {
  return useCallback(
    (measurement) => {
      if (!battlefieldDerivedValues) return;
      const { cover, rangeBand: band } = pinnedValues(measurement);
      updateCombatantState(activeTab, (st) => ({ ...st, cover }));
      if (band) setRangeBand(band);
      addNarrative(activeTab, infoLine(measuredText(measurement, band, cover)));
    },
    [battlefieldDerivedValues, updateCombatantState, activeTab, setRangeBand, addNarrative],
  );
}

/** Clear the log being looked at; copy both to the clipboard. */
function useLogActions({
  logView,
  currentTab,
  updateTab,
  activeTab,
  setNarrativeClearTime,
  firestoreNarrative,
  addNarrative,
}) {
  const doClearNarrative = useCallback(() => {
    if (logView === 'shared') setNarrativeClearTime(Date.now());
    else if (currentTab) updateTab(activeTab, (t) => ({ ...t, narrativeLog: [] }));
  }, [logView, currentTab, updateTab, activeTab, setNarrativeClearTime]);
  const doCopyNarrative = useCallback(() => {
    // No target open still leaves the shared feed to copy (v8.373.20).
    const localLog = currentTab?.narrativeLog ?? [];
    if (localLog.length === 0 && firestoreNarrative.length === 0) return;
    const text = formatForClipboard(localLog, firestoreNarrative, currentTab?.target.name || 'Unknown');
    copyText(text).then((ok) =>
      addNarrative(
        activeTab,
        infoLine(ok ? '📋 Combat log copied to clipboard.' : '⚠ Clipboard access denied - copy failed.'),
      ),
    );
  }, [currentTab, firestoreNarrative, activeTab, addNarrative]);
  return { doClearNarrative, doCopyNarrative };
}

/**
 * @param {object} args - currentTab, activeTab, isLocked, isDefencePending,
 *   capturePreActionSnapshot, pushHistory, historyTrack, updateTab,
 *   updateCombatantState, addNarrative, battlefieldDerivedValues,
 *   setRangeBand, logView, setNarrativeClearTime, firestoreNarrative.
 */
export default function useShellTargetActions(args) {
  const { updateCombatantState, activeTab } = args;
  const unpinCover = useCallback(() => {
    updateCombatantState(activeTab, (st) => ({ ...st, cover: { level: 'none', ap: 0 } }));
  }, [updateCombatantState, activeTab]);
  return {
    doResetTarget: useResetTarget(args),
    coverPinned: isCoverPinned(args.currentTab?.target),
    unpinCover,
    applyBattlefieldMeasurement: useApplyMeasurement(args),
    ...useLogActions(args),
  };
}
